/**
 * CAOS-572 — the page for ONE piece of the library: a three-tab component reference.
 *
 * What was here before was a catalogue entry — a name, a purpose, a group, an element tag and a
 * single example. What a person actually needs before they place a piece is what they can set on
 * it, what it gives back, what it looks like in each of its variants, where they are allowed to put
 * it, and what the thing they have to author looks like. None of that was on the page.
 *
 * ── NOTHING HERE KNOWS THE NAME OF ANY COMPONENT ─────────────────────────────
 * That is the property to protect, and it is checkable rather than believed: this file mentions no
 * component of the library by name, and its only input is what a piece declares about itself. A
 * piece that declares more gets a richer page with no change here; a piece that arrives from a
 * package it has never seen documents itself on arrival. The hand-kept map of examples this page
 * used to read — forty-nine builders in the workspace, keyed by element name — is deleted, because
 * a workspace that knows components by name is the exact arrangement this platform exists to remove.
 *
 * ── UNSAID IS NOT EMPTY, everywhere on this page ─────────────────────────────
 * A piece that has declared it raises no events reads "this piece raises no events". A piece that
 * has not said reads "not documented yet". Collapsing the two turns silence into a confident claim
 * nobody made, and this page is precisely where somebody would believe it.
 */
import {
  buildPlacement,
  defaultCell,
  descriptionCell,
  docsFor,
  exampleNodes,
  markupParts,
  placementGap,
  placementSectionJson,
  placementSentence,
  readOut,
  referenceMarkdown,
  requiredCell,
  runnableText,
  typeCell,
  type CatalogEntry,
  type CodePart,
  type ComponentDocs,
  type ComponentExample,
  type ComponentPlacement,
  type PageSeams,
  type PlacementNode,
} from '@caos/ui';
import { THEME_CHANGE_EVENT, applyThemePref, currentThemePref, nextThemePref, type ThemePref } from '../theme.js';

/**
 * The workspace's own seams, handed to any example that declares it needs one.
 *
 * There is ONE light/dark setting on this page, and this is it — the same one the control at the top
 * of the workspace and the Appearance preference in My Profile drive. An example is given it rather
 * than writing a second implementation, because two implementations of one setting disagree the
 * moment either is used, and a reference that demonstrates that is teaching the wrong thing.
 */
const PAGE_SEAMS: PageSeams = {
  theme: {
    current: () => currentThemePref(),
    cycle: () => applyThemePref(nextThemePref(currentThemePref())),
    onChange: (fn) => {
      const handler = (ev: Event): void => fn((ev as CustomEvent<ThemePref>).detail ?? currentThemePref());
      document.addEventListener(THEME_CHANGE_EVENT, handler);
      return () => document.removeEventListener(THEME_CHANGE_EVENT, handler);
    },
  },
};

/** Navigating within the workspace, handed down from the Setup surface. */
export type Navigate = (href: string) => void;

const ROUTE = '/setup/component-library';

// ── Small builders ────────────────────────────────────────────────────────────────────────────────

const el = (tag: string, className?: string, text?: string): HTMLElement => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

function link(text: string, href: string, navigate?: Navigate, className = 'setup-link'): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = className;
  a.href = href;
  a.textContent = text;
  a.addEventListener('click', (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigate?.(href);
  });
  return a;
}

/**
 * The three-state reading, in one place so every section gets the same answer.
 *
 * `undefined` — the piece has NOT SAID. `[]` — the piece says there are none. Anything else is the
 * contents. Three states, never two.
 */
type Declared<T> = { readonly state: 'unsaid' } | { readonly state: 'none' } | { readonly state: 'some'; readonly items: readonly T[] };

function declared<T>(items: readonly T[] | undefined): Declared<T> {
  if (items === undefined) return { state: 'unsaid' };
  if (items.length === 0) return { state: 'none' };
  return { state: 'some', items };
}

/** The sentence a section shows when it holds nothing — different for each of the two silences. */
function emptyLine(section: Declared<unknown>, none: string): HTMLElement {
  const note = el('p', 'cl-note');
  note.textContent = section.state === 'none' ? none : 'Not documented yet — this piece has not said.';
  if (section.state === 'unsaid') note.classList.add('cl-note--unsaid');
  return note;
}

// ── The header ────────────────────────────────────────────────────────────────────────────────────
// `placementSentence` and `readOut` moved to `@caos/ui` (CAOS-573) — the header sentence and
// `referenceMarkdown`'s copy of the same fact now read one function, not two kept in sync by hand.

function renderHeader(
  root: HTMLElement,
  entry: CatalogEntry,
  docs: ComponentDocs | undefined,
  navigate: Navigate | undefined,
  markdown: () => string,
): void {
  const trail = el('nav', 'cl-breadcrumb');
  trail.setAttribute('aria-label', 'Breadcrumb');
  trail.append(
    link('Component Library', ROUTE, navigate, 'cl-breadcrumb__link'),
    el('span', 'cl-breadcrumb__sep', '/'),
    link(entry.group, `${ROUTE}?group=${encodeURIComponent(entry.group)}`, navigate, 'cl-breadcrumb__link'),
    el('span', 'cl-breadcrumb__sep', '/'),
    el('span', 'cl-breadcrumb__here', entry.name),
  );

  const head = el('header', 'cl-head');
  const titles = el('div', 'cl-head__titles');
  titles.append(el('h1', 'cl-head__name', entry.name));
  if (entry.tags.length > 0) {
    const chips = el('div', 'cl-head__chips');
    for (const tag of entry.tags) chips.append(el('code', 'cl-chip', `<${tag}>`));
    titles.append(chips);
  } else {
    titles.append(el('span', 'cl-chip cl-chip--muted', 'registers no element'));
  }
  titles.append(el('p', 'cl-head__purpose', entry.purpose));
  titles.append(el('p', 'cl-head__placement', placementSentence(docs, entry)));

  // OTHER NAMES, on the page rather than only in the search index. Somebody who arrived by typing
  // "badge" and landed on a piece called something else needs to be told, once, that these are the
  // same thing — otherwise the next person types "badge" again and builds a second one.
  if (docs?.aliases?.length) {
    titles.append(el('p', 'cl-head__aliases', `Also called: ${docs.aliases.join(', ')}.`));
  }

  // THE CONTRACT AT A GLANCE. People build their own components OUT OF these, so the question they
  // arrive with is what this expects, what it emits and what it can contain — and they should be
  // able to answer it before deciding whether to read further, rather than after three tabs. Each
  // count keeps the three-state reading: a piece that has not said shows nothing rather than a zero.
  const glance = el('ul', 'cl-glance');
  const tally = (label: string, items: readonly unknown[] | undefined, none: string): void => {
    if (items === undefined) return;
    const item = el('li', 'cl-glance__item');
    item.append(
      el('span', 'cl-glance__count', items.length === 0 ? '—' : String(items.length)),
      el('span', 'cl-glance__label', items.length === 0 ? none : label),
    );
    glance.append(item);
  };
  tally('inputs a page can set', docs?.inputs, 'sets nothing');
  tally('events it raises', docs?.events, 'raises nothing');
  tally('slots it can hold', docs?.slots, 'holds nothing');
  tally('values it can be asked for', docs?.state, 'nothing to read back');
  tally('methods it can be driven by', docs?.methods, 'drives nothing');
  tally('worked examples', docs?.examples, 'no example');
  if (glance.children.length > 0) titles.append(glance);

  const actions = el('div', 'cl-head__actions');
  actions.append(copyControl('Copy for an assistant', markdown, 'caos-button'));
  head.append(titles, actions);

  root.append(trail, head);
}

/**
 * A control that puts something on the clipboard and says so.
 *
 * "Copy for an assistant" is the one on the header, and it is not a nicety on this platform: most
 * reference pages are now read by something that is about to write the code. What it copies is
 * generated from the same declarations the page is drawn from, so it cannot describe a piece that
 * has drifted from what is on screen.
 */
function copyControl(label: string, text: () => string, tag: 'caos-button' | 'button'): HTMLElement {
  const button = document.createElement(tag);
  if (tag === 'caos-button') button.setAttribute('variant', 'secondary');
  else button.className = 'cl-code__copy';
  button.textContent = label;
  button.addEventListener('click', () => {
    const done = () => {
      const was = button.textContent;
      button.textContent = 'Copied';
      window.setTimeout(() => {
        button.textContent = was;
      }, 1400);
    };
    const clip = navigator.clipboard;
    if (clip?.writeText) void clip.writeText(text()).then(done, () => undefined);
    else done();
  });
  return button;
}

// ── Tab one: Example ──────────────────────────────────────────────────────────────────────────────

/**
 * The stage an example stands on.
 *
 * Sized for what the piece DOES, not for how it looks standing still (CAOS-587). Most of the
 * catalogue opens nothing, so the default is the resting size with a sideways scroller for a piece
 * too wide for it. An example that opens a menu, a tooltip or a picker declares how much room that
 * needs and is put on a stage that reserves it and clips nothing — a scroller clips on BOTH axes,
 * which is what used to cut an opened menu off at the stage's edge.
 */
function buildStage(example: ComponentExample): HTMLElement {
  const stage = el('div', 'cl-stage');
  const needs = example.stage;
  const opens = needs?.opensBelow !== undefined || needs?.opensAbove !== undefined;
  const wide = needs?.needsWidth !== undefined;
  // The room is kept on exactly ONE box. A wide example keeps it inside its scroller (below);
  // everything else keeps it on the stage. Both would reserve it twice — which clips nothing, but
  // leaves a band of dead space around the example and puts the room somewhere it was never meant
  // to be. The scroller is a CHILD of the stage, so its padding still grows the stage and still
  // keeps the page clear beneath it for a panel that escapes the box.
  if (!wide) {
    if (needs?.opensBelow !== undefined) stage.style.setProperty('--cl-stage-room-below', `${needs.opensBelow}px`);
    if (needs?.opensAbove !== undefined) stage.style.setProperty('--cl-stage-room-above', `${needs.opensAbove}px`);
  }

  const nodes = exampleNodes(example);
  const pieces = nodes.map((node) => buildPlacement(node));

  let held: HTMLElement;
  if (needs?.contain !== undefined) {
    // A piece that lays itself over the whole screen, held inside its own box so it can be looked
    // at without taking the page over.
    held = el('div', 'cl-contained');
    held.style.setProperty('--cl-contained-height', `${needs.contain}px`);
    held.append(...pieces);
  } else if (pieces.length > 1 || needs?.alignEnd || needs?.arrange === 'column') {
    // Several pieces stand in a row unless the example says otherwise. Stacking is not cosmetic: a
    // placeholder standing in for a REGION says something different in a column than in a line.
    const classes = ['cl-row'];
    if (needs?.arrange === 'column') classes.push('cl-row--column');
    if (needs?.alignEnd) classes.push('cl-row--end');
    held = el('div', classes.join(' '));
    held.append(...pieces);
  } else {
    held = pieces[0] ?? el('div');
  }

  // An example too wide for the stage scrolls sideways inside its own box. That box clips on both
  // axes, so anything the example opens has to be kept clear INSIDE it rather than on the stage —
  // otherwise being wide and opening something are mutually exclusive, and the first example that
  // needed both would silently lose one (CAOS-655).
  if (wide) {
    const fit = el('div', 'cl-stage__fit');
    fit.style.setProperty('--cl-fit-width', `${needs!.needsWidth!}px`);
    if (needs?.opensBelow !== undefined) fit.style.setProperty('--cl-fit-room-below', `${needs.opensBelow}px`);
    if (needs?.opensAbove !== undefined) fit.style.setProperty('--cl-fit-room-above', `${needs.opensAbove}px`);
    held.classList.add('cl-stage__wide');
    fit.append(held);
    stage.append(fit);
  } else if (opens || needs?.contain !== undefined) {
    stage.append(held);
  } else {
    const fit = el('div', 'cl-stage__fit');
    fit.append(held);
    stage.append(fit);
  }
  return stage;
}

/**
 * One named example: its sentence, the live piece, and the code that drew it.
 *
 * The RESET control is the generic answer to a problem that used to be solved per component. Several
 * pieces make themselves disappear when you use them — a drawer closes, a banner is dismissed, a
 * record is taken off a strip — and each of those carried a hand-written re-open wrapper in the
 * workspace. Rebuilding the example from its declaration does all of them at once, and does it for
 * examples nobody has written yet.
 */
function renderExample(example: ComponentExample, entry: CatalogEntry, docs: ComponentDocs | undefined): HTMLElement {
  const holder = el('div', 'cl-example');
  holder.dataset.example = example.key;
  holder.append(el('p', 'cl-example__summary', example.summary));

  const mount = el('div', 'cl-example__mount');
  const draw = (): void => {
    mount.replaceChildren();
    const stage = buildStage(example);
    mount.append(stage);
    // The page around the example plays its half — and says so, rather than passing host behaviour
    // off as something the piece does on its own.
    example.host?.run(stage, PAGE_SEAMS);
  };
  draw();

  const bar = el('div', 'cl-example__bar');
  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'cl-example__reset';
  reset.textContent = 'Reset the example';
  reset.addEventListener('click', draw);
  bar.append(reset);
  holder.append(mount, bar);

  if (example.host) {
    const note = el('p', 'cl-note cl-note--host');
    note.textContent = `The page around it: ${example.host.summary}`;
    holder.append(note);
  }

  holder.append(renderCodePanel(example, entry, docs));
  return holder;
}

/**
 * The code panel — two VIEWS over the same example, and file tabs inside a view.
 *
 * Two different people open this page and they need different code. Somebody BUILDING A PAGE never
 * writes markup: they place the piece, and the placement is metadata. Somebody WRITING A COMPONENT
 * composes ours inside theirs by element name, as markup — and that is who a component reference is
 * mostly for. So the panel carries both, and which one opens first follows from what the piece
 * declared about where it may be placed: a piece meant to be placed opens on its placement, a piece
 * meant to be composed opens on its markup.
 *
 * Neither view is a sample somebody wrote beside the component. The Markup view is printed from the
 * same tree that built the preview above, so it cannot drift from what is on screen. The Placement
 * view either is that tree too, printed as the real stored-page shape — or it is not shown as code at
 * all: CAOS-571 — a node the page model has no field for (see {@link placementGap}) fails loudly,
 * naming what is missing, rather than printing something that reads like metadata and is not.
 */
function renderCodePanel(example: ComponentExample, entry: CatalogEntry, docs: ComponentDocs | undefined): HTMLElement {
  const nodes = exampleNodes(example);
  const gap = nodes.map(placementGap).find((g): g is string => g !== undefined);
  const placeable = gap === undefined && (docs?.placements?.length ?? 0) > 0 && entry.tags.length > 0;

  type View = { key: string; label: string; parts: readonly CodePart[] } | { key: 'placement'; label: string; gap: string };
  const views: View[] = [
    gap === undefined
      ? {
          key: 'placement',
          label: 'Placement',
          parts: [{ name: 'page.json', language: 'json', text: placementSectionJson(nodes, example.label) }],
        }
      : { key: 'placement', label: 'Placement', gap },
    { key: 'markup', label: 'Markup', parts: markupParts(nodes) },
  ];
  if (!placeable) views.reverse();

  const panel = el('section', 'cl-code');
  const head = el('div', 'cl-code__head');
  const viewTabs = el('div', 'cl-code__views');
  const fileTabs = el('div', 'cl-code__files');
  const body = el('div', 'cl-code__body');
  const copyHolder = el('div', 'cl-code__tools');
  head.append(viewTabs, fileTabs, copyHolder);
  panel.append(head, body);

  let view = views[0]!;
  let part = 'parts' in view ? view.parts[0] : undefined;

  const paint = (): void => {
    for (const button of viewTabs.querySelectorAll<HTMLButtonElement>('button')) {
      button.setAttribute('aria-pressed', String(button.dataset.view === view.key));
    }
    fileTabs.replaceChildren();
    if ('gap' in view) {
      // LOUD, and specific — not code, so it is not mistaken for code that happens to be broken.
      const notice = document.createElement('caos-error-panel');
      notice.setAttribute('heading', 'This example cannot be shown as a page placement');
      notice.textContent = view.gap;
      body.replaceChildren(notice);
      copyHolder.replaceChildren();
      return;
    }
    const parts = view.parts;
    if (parts.length > 1) {
      for (const candidate of parts) {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'cl-code__file';
        tab.textContent = candidate.name;
        tab.setAttribute('aria-pressed', String(candidate.name === part!.name));
        tab.addEventListener('click', () => {
          part = candidate;
          paint();
        });
        fileTabs.append(tab);
      }
    }
    body.replaceChildren(codeBlock(part!));
    // The copy is the WHOLE VIEW, not the file being read. An example that sets a list of rows is
    // two parts, and copying only the markup hands somebody an element that draws nothing. A
    // copyable example that does not run when pasted is worse than no copy control.
    copyHolder.replaceChildren(copyControl('Copy', () => runnableText(parts), 'button'));
  };

  for (const candidate of views) {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'cl-code__view';
    tab.dataset.view = candidate.key;
    tab.textContent = candidate.label;
    tab.addEventListener('click', () => {
      view = candidate;
      part = 'parts' in candidate ? candidate.parts[0] : undefined;
      paint();
    });
    viewTabs.append(tab);
  }
  paint();

  // WHAT THE SNIPPET ASSUMES. Copying something that quietly needs setup the page never showed is
  // the way a copy control stops being trusted, so anything the paste does not carry is said here.
  const assumes: string[] = ['the library’s elements are registered on the page you paste it into'];
  if (example.host) assumes.push(`the surrounding page ${example.host.summary}`);
  panel.append(el('p', 'cl-note cl-code__assumes', `Pasting this needs ${readOut(assumes)}.`));
  return panel;
}

/** One block of code: line numbers down the side, the highlighted source beside them. */
function codeBlock(part: CodePart): HTMLElement {
  const holder = el('div', 'cl-code__block');
  const lines = part.text.split('\n');
  const gutter = el('div', 'cl-code__gutter');
  gutter.setAttribute('aria-hidden', 'true');
  for (let n = 1; n <= lines.length; n += 1) gutter.append(el('span', 'cl-code__line', String(n)));
  const pre = el('pre', 'cl-code__pre');
  const code = el('code', `cl-code__source cl-code__source--${part.language}`);
  for (const token of highlight(part.text, part.language)) {
    code.append(token.kind === 'plain' ? document.createTextNode(token.text) : el('span', `cl-tok cl-tok--${token.kind}`, token.text));
  }
  pre.append(code);
  holder.append(gutter, pre);
  return holder;
}

/**
 * Syntax highlighting, built here rather than pulled in.
 *
 * Everything on this platform is constructed through the DOM rather than raw markup so a strict
 * content-security policy holds, and no external script is loaded anywhere. A highlighter is a small
 * enough thing that taking a dependency on one — and with it a script origin — would be a poor trade.
 */
type Token = { kind: 'plain' | 'key' | 'string' | 'number' | 'literal' | 'tag' | 'attr' | 'comment'; text: string };

const PATTERNS: Readonly<Record<CodePart['language'], readonly { kind: Token['kind']; re: RegExp }[]>> = {
  json: [
    { kind: 'key', re: /"(?:[^"\\]|\\.)*"(?=\s*:)/y },
    { kind: 'string', re: /"(?:[^"\\]|\\.)*"/y },
    { kind: 'number', re: /-?\d+(?:\.\d+)?/y },
    { kind: 'literal', re: /\b(?:true|false|null)\b/y },
  ],
  html: [
    { kind: 'comment', re: /<!--[\s\S]*?-->/y },
    { kind: 'tag', re: /<\/?[a-z][a-z0-9-]*/y },
    { kind: 'string', re: /"(?:[^"\\]|\\.)*"/y },
    { kind: 'attr', re: /[a-z-]+(?==)/y },
  ],
  js: [
    { kind: 'comment', re: /\/\/[^\n]*/y },
    { kind: 'string', re: /"(?:[^"\\]|\\.)*"/y },
    { kind: 'number', re: /-?\d+(?:\.\d+)?/y },
    { kind: 'literal', re: /\b(?:true|false|null|undefined)\b/y },
  ],
};

function highlight(text: string, language: CodePart['language']): readonly Token[] {
  const rules = PATTERNS[language];
  const out: Token[] = [];
  let plain = '';
  let at = 0;
  while (at < text.length) {
    let matched: Token | undefined;
    for (const rule of rules) {
      rule.re.lastIndex = at;
      const hit = rule.re.exec(text);
      if (hit && hit.index === at) {
        matched = { kind: rule.kind, text: hit[0] };
        break;
      }
    }
    if (matched) {
      if (plain !== '') {
        out.push({ kind: 'plain', text: plain });
        plain = '';
      }
      out.push(matched);
      at += matched.text.length;
    } else {
      plain += text[at];
      at += 1;
    }
  }
  if (plain !== '') out.push({ kind: 'plain', text: plain });
  return out;
}

/**
 * The Example tab: a picker over the named examples, then the one being read.
 *
 * The picker appears only when a piece has more than one — a picker over a single example is
 * furniture. Every piece has at least one, with no exceptions: a section headed "Working example"
 * that contains a sentence telling the reader to go and look at the thing on another screen is a
 * broken promise, and sending somebody elsewhere defeats the point of a library.
 */
function renderExampleTab(entry: CatalogEntry, docs: ComponentDocs | undefined): HTMLElement {
  const panel = el('div', 'cl-panel');
  const examples = declared(docs?.examples);
  if (examples.state !== 'some') {
    const gap = document.createElement('caos-error-panel');
    gap.setAttribute('heading', 'This piece has no working example');
    gap.textContent =
      `No worked example has been written for ${entry.name}. Every piece is supposed to have one, ` +
      'so this is a gap in the library rather than a property of the piece.';
    panel.append(gap);
    return panel;
  }

  const list = examples.items as readonly ComponentExample[];
  const body = el('div', 'cl-examples');

  if (list.length > 1) {
    const picker = document.createElement('caos-segmented-filter');
    picker.setAttribute('aria-label', 'Examples');
    picker.setAttribute('value', list[0]!.key);
    (picker as unknown as { options: unknown }).options = list.map((x) => ({ value: x.key, label: x.label }));
    picker.addEventListener('change', (ev) => {
      const value = (ev as CustomEvent<{ value: string }>).detail?.value;
      const chosen = list.find((x) => x.key === value);
      if (chosen) body.replaceChildren(renderExample(chosen, entry, docs));
    });
    panel.append(picker);
  }
  body.append(renderExample(list[0]!, entry, docs));
  panel.append(body);
  return panel;
}

// ── Tab two: Develop ──────────────────────────────────────────────────────────────────────────────

function prose(heading: string, text: string | undefined, unsaid: string): HTMLElement {
  const section = document.createElement('caos-detail-section');
  section.setAttribute('heading', heading);
  if (text === undefined) {
    const note = el('p', 'cl-note cl-note--unsaid');
    note.textContent = unsaid;
    section.append(note);
  } else {
    section.append(el('p', 'cl-prose', text));
  }
  return section;
}

function renderDevelopTab(entry: CatalogEntry, docs: ComponentDocs | undefined, navigate?: Navigate): HTMLElement {
  const panel = el('div', 'cl-panel');

  panel.append(
    prose(
      'Placing it',
      docs?.placing,
      'Not documented yet — this piece has not said how it is placed in a page and its region.',
    ),
  );

  // Styling — the tokens the piece honours. A subscriber restyling the platform needs to know which
  // token moves this piece, and guessing is how a brand ends up half-applied.
  const tokens = declared(docs?.tokens);
  const styling = document.createElement('caos-detail-section');
  styling.setAttribute('heading', 'Styling');
  if (tokens.state === 'some') {
    styling.append(
      table(
        [
          { key: 'token', label: 'Design token' },
          { key: 'controls', label: 'What it controls' },
        ],
        tokens.items.map((t, i) => ({ id: `t${i}`, cells: { token: { node: el('code', 'cl-chip', t.token) }, controls: t.controls } })),
      ),
    );
  } else {
    styling.append(emptyLine(tokens, 'This piece honours no design tokens of its own.'));
  }
  panel.append(styling);

  // When to use it, and what to use instead — the single most useful paragraph in any reference.
  const use = document.createElement('caos-detail-section');
  use.setAttribute('heading', 'When to use it, and what to use instead');
  if (docs?.whenToUse === undefined) {
    use.append(emptyLine({ state: 'unsaid' }, ''));
  } else {
    use.append(el('p', 'cl-prose', docs.whenToUse));
  }
  const alternatives = declared(docs?.insteadUse);
  if (alternatives.state === 'some') {
    const dl = el('dl', 'cl-alts');
    for (const alt of alternatives.items) {
      const dt = el('dt', 'cl-alts__name');
      dt.append(link(alt.name, `${ROUTE}/${alt.name}`, navigate));
      dl.append(dt, el('dd', 'cl-alts__when', alt.when));
    }
    use.append(dl);
  } else if (alternatives.state === 'none') {
    use.append(el('p', 'cl-note', 'Nothing in the library is easily confused with this piece.'));
  }
  panel.append(use);

  panel.append(
    prose(
      'Accessibility',
      docs?.accessibility,
      'Not documented yet — this piece has not said what it manages on the author’s behalf.',
    ),
  );

  // CAOS-602 — only for a piece that has something to say. Almost none do, and a "not documented
  // yet" line on sixty-one pages would read as sixty-one omissions rather than one exception.
  if (docs?.provenance !== undefined) {
    panel.append(prose('Where the artwork comes from', docs.provenance, ''));
  }

  // The one thing on this tab that is not a declaration: where the piece lives, for somebody who
  // wants to read it. It comes off the catalogue entry, which every piece has.
  const where = document.createElement('caos-detail-section');
  where.setAttribute('heading', 'Where it lives');
  const kv = document.createElement('caos-key-value-row');
  kv.setAttribute('label', 'Source');
  // CAOS-387 — a piece arriving from an installed package carries no in-workspace file at all;
  // saying so is the honest answer, not a guess dressed up as a path.
  kv.setAttribute(
    'value',
    entry.file ? `caos-ui/src/components/${entry.file}` : 'Installed from a package — not part of this workspace.',
  );
  where.append(kv);
  panel.append(where);

  return panel;
}

// ── Tab three: Specifications ─────────────────────────────────────────────────────────────────────

interface Column {
  readonly key: string;
  readonly label: string;
}
type Row = { id: string; cells: Record<string, unknown> };

function table(columns: readonly Column[], rows: readonly Row[]): HTMLElement {
  const node = document.createElement('caos-data-table');
  (node as unknown as { columns: unknown; rows: unknown }).columns = columns;
  (node as unknown as { columns: unknown; rows: unknown }).rows = rows;
  return node;
}

function specSection(heading: string, section: Declared<unknown>, none: string, build: () => HTMLElement): HTMLElement {
  const node = document.createElement('caos-detail-section');
  node.setAttribute('heading', heading);
  node.append(section.state === 'some' ? build() : emptyLine(section, none));
  return node;
}

// `requiredCell` and `defaultCell` moved to `@caos/ui` (CAOS-573) — this tab's table cells and
// `referenceMarkdown`'s table rows read the same two functions rather than two hand-kept copies.

function renderSpecificationsTab(docs: ComponentDocs | undefined): HTMLElement {
  const panel = el('div', 'cl-panel');

  const inputs = declared(docs?.inputs);
  panel.append(
    specSection('Inputs', inputs, 'A page sets nothing on this piece.', () =>
      table(
        [
          { key: 'name', label: 'Name' },
          { key: 'description', label: 'Description' },
          { key: 'type', label: 'Type' },
          { key: 'default', label: 'Default' },
          { key: 'required', label: 'Required' },
        ],
        (inputs as { items: readonly NonNullable<ComponentDocs['inputs']>[number][] }).items.map((input, i) => ({
          id: `in${i}`,
          cells: {
            // Both names, deliberately: an author writes the property in code and the attribute in
            // markup, and making somebody transliterate is a tax paid on every line.
            name: { node: nameCell(input) },
            // CAOS-653 — carries what reading it back gives, where that is not what writing sets.
            description: descriptionCell(input),
            // A type alone says "text" for an input that only ever takes one of four words, which
            // is true and useless. Where the piece declared the four, they are the answer — and
            // since CAOS-653 the same cell distinguishes a closed set from an open one with a
            // fallback, through the function the markdown reference renders its column from.
            type: typeCell(input),
            default: defaultCell(input.default),
            required: requiredCell(input.required),
          },
        })),
      ),
    ),
  );

  const events = declared(docs?.events);
  panel.append(
    specSection('Events', events, 'This piece raises no events.', () =>
      table(
        [
          { key: 'name', label: 'Name' },
          { key: 'when', label: 'When it fires' },
          { key: 'carries', label: 'What it carries' },
        ],
        (events as { items: readonly NonNullable<ComponentDocs['events']>[number][] }).items.map((e, i) => ({
          id: `ev${i}`,
          cells: { name: { node: el('code', 'cl-chip', e.name) }, when: e.when, carries: e.carries },
        })),
      ),
    ),
  );

  // What a page may READ BACK but cannot set. This section exists because writing the documentation
  // found it missing: a menu exposes whether its panel is open, and that is neither an input nor a
  // method, so without a table of its own a reader never learns it is there.
  const state = declared(docs?.state);
  panel.append(
    specSection('Readable state', state, 'Nothing on this piece can be read back.', () =>
      table(
        [
          { key: 'name', label: 'Name' },
          { key: 'type', label: 'Type' },
          { key: 'description', label: 'What it tells you' },
        ],
        (state as { items: readonly NonNullable<ComponentDocs['state']>[number][] }).items.map((x, i) => ({
          id: `st${i}`,
          cells: { name: { node: el('code', 'cl-chip', x.name) }, type: x.type, description: x.description },
        })),
      ),
    ),
  );

  const slots = declared(docs?.slots);
  panel.append(
    specSection('Slots', slots, 'Nothing goes inside this piece.', () =>
      table(
        [
          { key: 'name', label: 'Name' },
          { key: 'takes', label: 'What goes in it' },
        ],
        (slots as { items: readonly NonNullable<ComponentDocs['slots']>[number][] }).items.map((s, i) => ({
          id: `sl${i}`,
          cells: { name: { node: el('code', 'cl-chip', s.name === '' ? 'the default slot' : s.name) }, takes: s.takes },
        })),
      ),
    ),
  );

  const tokens = declared(docs?.tokens);
  panel.append(
    specSection('Styling hooks', tokens, 'This piece honours no design tokens of its own.', () =>
      table(
        [
          { key: 'token', label: 'Design token' },
          { key: 'controls', label: 'What it controls' },
        ],
        (tokens as { items: readonly NonNullable<ComponentDocs['tokens']>[number][] }).items.map((t, i) => ({
          id: `tk${i}`,
          cells: { token: { node: el('code', 'cl-chip', t.token) }, controls: t.controls },
        })),
      ),
    ),
  );

  // Tokens are half of restyling; parts are the other half. A token changes a value the piece already
  // reads; a part lets somebody reach a piece of its inside the component never exposed a value for.
  const parts = declared(docs?.parts);
  panel.append(
    specSection('Styling parts', parts, 'This piece exposes none of its insides to a stylesheet.', () =>
      table(
        [
          { key: 'part', label: 'Part' },
          { key: 'is', label: 'Which piece of it' },
          // CAOS-653 — a part can be exposed only in some states, and was declared as though it is
          // always there. "Always" is written out rather than left blank so the ordinary case reads
          // as something the piece claimed instead of something nobody filled in.
          { key: 'when', label: 'When it is there' },
        ],
        (parts as { items: readonly NonNullable<ComponentDocs['parts']>[number][] }).items.map((x, i) => ({
          id: `pt${i}`,
          cells: { part: { node: el('code', 'cl-chip', `::part(${x.part})`) }, is: x.is, when: x.when ?? 'Always' },
        })),
      ),
    ),
  );

  const methods = declared(docs?.methods);
  panel.append(
    specSection('Methods', methods, 'Nothing else drives this piece by calling it.', () =>
      table(
        [
          { key: 'name', label: 'Name' },
          { key: 'description', label: 'Description' },
          { key: 'args', label: 'Arguments' },
        ],
        (methods as { items: readonly NonNullable<ComponentDocs['methods']>[number][] }).items.map((m, i) => ({
          id: `me${i}`,
          cells: {
            name: { node: el('code', 'cl-chip', `${m.name}()`) },
            description: m.description,
            args:
              m.args === undefined
                ? 'not stated'
                : m.args.length === 0
                  ? 'takes none'
                  : m.args.map((a) => `${a.name}: ${a.type} — ${a.description}`).join('; '),
          },
        })),
      ),
    ),
  );

  return panel;
}

function nameCell(input: NonNullable<ComponentDocs['inputs']>[number]): HTMLElement {
  const holder = el('div', 'cl-names');
  holder.append(el('code', 'cl-chip', input.name));
  holder.append(
    input.attribute === undefined
      ? el('span', 'cl-names__note', 'property only')
      : el('code', 'cl-chip cl-chip--muted', input.attribute),
  );
  // CAOS-653 — which way it can actually be set. An attribute with no accessor behind it is the
  // ordinary case in this library (attributes are not reflected into properties), so an author who
  // assumes both work sets an expando the piece never reads and gets no error saying so.
  if (input.attribute !== undefined) {
    holder.append(el('span', 'cl-names__note', input.property === true ? 'also a property' : 'markup only'));
  }
  return holder;
}

// `referenceMarkdown` moved to `@caos/ui` (CAOS-573) — it is imported above and re-exported below,
// unchanged in behaviour, so both this page's "Copy for an assistant" control and a public build
// step read the same function against the same declarations. See `caos-ui/src/reference-markdown.ts`.

// ── The page ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The reference for one piece. Everything on it is read from what the piece declared.
 *
 * `docs` defaults to what the compiled library holds, which is where every piece's declaration lives
 * today. It is a PARAMETER rather than a lookup buried inside because the compiled library is not
 * where declarations will come from for long: a component arriving in a package a subscriber
 * installs brings its own, and this page has to draw it without knowing it was coming. Handing the
 * declaration in is the seam that makes that true — and it is the same seam that lets a test prove
 * this page can document a component it has never heard of.
 */
export function renderComponentReference(
  entry: CatalogEntry,
  navigate?: Navigate,
  docs: ComponentDocs | undefined = docsFor(entry.name),
): HTMLElement {
  const root = el('div', 'cl-reference');

  renderHeader(root, entry, docs, navigate, () => referenceMarkdown(entry, docs));

  // The reference is drawn with the library's own tab strip — the reference for the pieces is built
  // out of the pieces, which is the cheapest available test that they work.
  const tabs = document.createElement('caos-tab-strip');
  tabs.setAttribute('aria-label', 'Reference');
  tabs.setAttribute('value', 'example');
  (tabs as unknown as { items: unknown }).items = [
    { value: 'example', label: 'Example' },
    { value: 'develop', label: 'Develop' },
    { value: 'specifications', label: 'Specifications' },
  ];

  const panels: [string, HTMLElement][] = [
    ['example', renderExampleTab(entry, docs)],
    ['develop', renderDevelopTab(entry, docs, navigate)],
    ['specifications', renderSpecificationsTab(docs)],
  ];
  for (const [value, panel] of panels) {
    panel.slot = 'panel';
    panel.dataset.value = value;
    tabs.append(panel);
  }
  root.append(tabs);
  return root;
}

/** Exported for the tests, so what a piece declares can be checked without opening the page. */
export { placementSentence, declared, referenceMarkdown };
export type { PlacementNode, ComponentPlacement };
